import React, { useState } from 'react';

import { useNavigate } from '../i18n/router';
import { useTranslation } from 'react-i18next';
import { sendPasswordResetEmail } from '../Services';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useSnackbar } from './SnackbarProvider';

const ForgotPasswordScreen = () => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const { showSnackbar } = useSnackbar();

  const isEmailValid = (emailVal: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(emailVal);
  };

  const handlePasswordReset = async () => {
    if (!email.trim()) {
      showSnackbar(t('forgot.errors.emptyEmail'), 'error');
      return;
    }

    if (!isEmailValid(email)) {
      showSnackbar(t('forgot.errors.invalidEmail'), 'error');
      return;
    }

    try {
      await sendPasswordResetEmail(email);
      showSnackbar(t('forgot.success'), 'success');
      navigate(-1);
    } catch (error) {
      showSnackbar(t('forgot.errors.failed'), 'error');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handlePasswordReset();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'linear-gradient(180deg, #FFF8EC 0%, #FAF3E2 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          maxWidth: isDesktop ? 800 : 420,
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          backgroundColor: 'white',
        }}
      >
        {/* Left panel (desktop only) */}
        {isDesktop && (
          <Box
            sx={{
              flex: 1,
              background: 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 5,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.06)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -40,
                left: -40,
                width: 160,
                height: 160,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.04)',
              }}
            />

            <LockResetIcon sx={{ fontSize: 80, color: 'rgba(255,255,255,0.3)', mb: 3 }} />
            <Typography
              variant="h5"
              sx={{ color: 'white', fontWeight: 700, textAlign: 'center', mb: 1.5, lineHeight: 1.3 }}
            >
              {t('forgot.brandTagline')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.75)', textAlign: 'center', maxWidth: 260, lineHeight: 1.6 }}
            >
              {t('forgot.brandSubtitle')}
            </Typography>
          </Box>
        )}

        {/* Right panel - form */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 3.5, sm: 5 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {/* Back + Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                backgroundColor: '#f5f5f5',
                '&:hover': { backgroundColor: '#eee' },
                width: 40,
                height: 40,
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 20, color: '#555' }} />
            </IconButton>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#333', fontSize: { xs: '1.2rem', sm: '1.4rem' } }}>
                {t('forgot.title')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', fontSize: '0.8rem' }}>
                {t('forgot.subtitle')}
              </Typography>
            </Box>
          </Box>

          {/* Mobile logo */}
          {!isDesktop && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <LockResetIcon sx={{ fontSize: 56, color: '#2e7d32', mb: 1 }} />
            </Box>
          )}

          {/* Email field */}
          <TextField
            fullWidth
            variant="outlined"
            label={t('forgot.fields.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="email"
            type="email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#f8f9fa',
                '&:hover': { backgroundColor: '#f0f1f2' },
                '&.Mui-focused': { backgroundColor: 'white' },
              },
            }}
          />

          {/* Submit */}
          <Button
            fullWidth
            variant="contained"
            onClick={handlePasswordReset}
            sx={{
              py: 1.5,
              borderRadius: 3,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #D89D2F 0%, #B5811F 100%)',
              boxShadow: '0 4px 14px rgba(46,125,50,0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #B5811F 0%, #8C6418 100%)',
                boxShadow: '0 6px 20px rgba(46,125,50,0.4)',
              },
            }}
          >
            {t('forgot.submit')}
          </Button>

          {/* Cancel */}
          <Button
            fullWidth
            variant="text"
            onClick={() => navigate(-1)}
            sx={{
              mt: 1.5,
              textTransform: 'none',
              color: '#888',
              fontWeight: 500,
              '&:hover': { color: '#555', backgroundColor: 'transparent' },
            }}
          >
            {t('backToLogin')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPasswordScreen;
